import React from 'react'
import { Box, Grid } from '@material-ui/core'
import { PageHeader } from 'components/page-header'
import { useQuery } from '@apollo/client'
import { GET_COACH_ANALYTICS } from './query'
import useStyles from './styles'
import UpgradeModal from 'components/upgrade-modal'
import {
  PaymentsByMonthChart,
  EarningsCard,
  PrivateLessonRevenueCard,
  PrivateLessonMonthlyChart,
  TopBookedLessonsChart,
  GroupEventRevenueCard,
  GroupEventMonthlyChart,
  TopGroupEventsChart,
  LeadsMonthlyChart,
  LeadCard,
  ConversionRateCard,
} from './components'

const CoachAnalytics = () => {
  const classes = useStyles()
  const { data, loading, error } = useQuery(GET_COACH_ANALYTICS)

  return (
    <UpgradeModal
      pageName="coach-analytics"
      title="Upgrade to Access Analytics"
      description="Get detailed insights into your coaching business with our premium analytics dashboard. Track earnings, leads, and performance metrics to grow your coaching practice."
    >
      <>
        <PageHeader title="PGA Coach Analytics" fixedMobileActions />
        <Box className={classes.container} style={{ position: 'relative' }}>
          {/* Earnings cards */}
          <Grid container spacing={3} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={4}>
              <EarningsCard
                data={{
                  total: data?.pgaCoachAnalytics?.earnings?.total,
                  source: 'Earnings through PGA Coach',
                }}
                loading={loading}
                error={error}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <PrivateLessonRevenueCard
                data={data?.pgaCoachAnalytics?.privateLessonTypes}
                loading={loading}
                error={error}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <GroupEventRevenueCard
                data={data?.pgaCoachAnalytics?.groupEvents}
                loading={loading}
                error={error}
              />
            </Grid>
          </Grid>

          {/* Leads cards */}
          <Grid container spacing={3} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={4}>
              <LeadCard
                title="Total Leads"
                value={data?.pgaCoachAnalytics?.leads?.totalCount}
                loading={loading}
                error={error}
                tooltip="Total number of leads generated through PGA Coach"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LeadCard
                title="Unviewed Leads"
                value={data?.pgaCoachAnalytics?.leads?.unviewedCount}
                loading={loading}
                error={error}
                tooltip="Number of new leads that have not been viewed by you"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ConversionRateCard
                value={`${data?.pgaCoachAnalytics?.leads?.conversionRate}%`}
                loading={loading}
                error={error}
                tooltip="Percentage of leads that have been converted into students"
              />
            </Grid>
          </Grid>

          {/* Leads and Payments charts */}
          <Grid container spacing={3} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={6}>
              <Box className={classes.chart}>
                <LeadsMonthlyChart
                  data={data?.pgaCoachAnalytics?.leads?.byMonth}
                  title="Leads Acquired By Month"
                  loading={loading}
                  error={error}
                  tooltip="Monthly trend of leads generated through PGA Coach platform"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.chart}>
                <PaymentsByMonthChart
                  data={data?.pgaCoachAnalytics?.earnings?.byMonth}
                  title="Payments By Month"
                  loading={loading}
                  error={error}
                  tooltip="Monthly revenue from all coaching activities through PGA Coach"
                />
              </Box>
            </Grid>
          </Grid>

          {/* Group Events and Private Lessons charts */}
          <Grid container spacing={3} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={6}>
              <Box className={classes.chart}>
                <GroupEventMonthlyChart
                  data={data?.pgaCoachAnalytics?.groupEvents?.byMonth}
                  title="Group Event Enrollments By Month"
                  loading={loading}
                  error={error}
                  tooltip="Monthly enrollment trends for group events and programs"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={classes.chart}>
                <PrivateLessonMonthlyChart
                  data={data?.pgaCoachAnalytics?.privateLessonTypes?.byMonth}
                  title="Private Lessons By Month"
                  loading={loading}
                  error={error}
                  tooltip="Monthly booking trends for private lesson sessions"
                />
              </Box>
            </Grid>
          </Grid>

          {/* Pie charts */}
          <Grid container spacing={3} style={{ marginBottom: '24px' }}>
            <Grid item xs={12} md={6}>
              <Box className={classes.chart} style={{ minHeight: '380px' }}>
                <TopBookedLessonsChart
                  data={data?.pgaCoachAnalytics?.privateLessonTypes?.topBooked}
                  loading={loading}
                  error={error}
                  tooltip="Most popular lesson types by bookings, unique students, and revenue"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box className={classes.chart} style={{ minHeight: '380px' }}>
                <TopGroupEventsChart
                  data={data?.pgaCoachAnalytics?.groupEvents?.topEarningEvents}
                  loading={loading}
                  error={error}
                  tooltip="Highest performing group events by registrations and revenue"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </>
    </UpgradeModal>
  )
}

export default CoachAnalytics
